import { useState, useCallback } from 'react';
import apiClient from 'utils/apiClient';

const useGuestSessions = () => {
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, expired: 0, revoked: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const fetchSessions = useCallback(async (page = 0, size = 20, status = 'all') => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(
        `/identity-service/api/v1/admin/guest-sessions?page=${page}&size=${size}&status=${status}`
      );
      const data = response.data;
      setSessions(data.content || []);
      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);
      return data;
    } catch (err) {
      setError(err.message);
      console.error('[useGuestSessions] Error fetching sessions:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/identity-service/api/v1/admin/guest-sessions/stats');
      const data = response.data;
      setStats({
        total: data.total || 0,
        active: data.active || 0,
        expired: data.expired || 0,
        revoked: data.revoked || 0,
      });
      console.log('[fetchStats] Stats updated:', data);
      return data;
    } catch (err) {
      setError(err.message);
      console.error('[useGuestSessions] Error fetching stats:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const revokeSession = useCallback(async (sessionId, reason = 'Revoked by admin') => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post(
        `/identity-service/api/v1/admin/guest-sessions/${sessionId}/revoke`,
        { reason }
      );
      return { success: true, data: response.data };
    } catch (err) {
      setError(err.message);
      console.error('[useGuestSessions] Error revoking session:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteSession = useCallback(async (sessionId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.delete(
        `/identity-service/api/v1/admin/guest-sessions/${sessionId}`
      );
      return { success: true, data: response.data };
    } catch (err) {
      setError(err.message);
      console.error('[useGuestSessions] Error deleting session:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const wipeExpiredSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.delete('/identity-service/api/v1/admin/guest-sessions/expired');
      return { success: true, data: response.data };
    } catch (err) {
      setError(err.message);
      console.error('[useGuestSessions] Error wiping expired sessions:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const wipeAllSessions = useCallback(async (olderThanDays = 30) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.delete(
        `/identity-service/api/v1/admin/guest-sessions/all?olderThanDays=${olderThanDays}`
      );
      console.log('[wipeAllSessions] Response:', response.status, response.data);
      return { success: true, data: response.data };
    } catch (err) {
      setError(err.message);
      console.error('[useGuestSessions] Error wiping all sessions:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    sessions,
    stats,
    loading,
    error,
    totalPages,
    totalElements,
    fetchSessions,
    fetchStats,
    revokeSession,
    deleteSession,
    wipeExpiredSessions,
    wipeAllSessions,
  };
};

export default useGuestSessions;